/**
 * Las imágenes del documento, resueltas antes de imprimir.
 *
 * ── Por qué hace falta este paso ────────────────────────────────────────────
 * jsPDF incrusta imágenes de forma SÍNCRONA: quiere los bytes en la mano. Una
 * URL no le sirve. Así que entre «tengo el documento» e «imprimo el documento»
 * hay que bajar las fotos y convertirlas, y eso es asíncrono.
 *
 * Vive aparte para que `construirPdf` siga siendo síncrono y se pueda probar
 * sin red: recibe un mapa ya resuelto y dibuja lo que haya.
 *
 * ── Una foto nunca tumba una exportación ────────────────────────────────────
 * Si la descarga falla —el enlace caducó, el bucket cambió de permisos, no hay
 * red— se devuelve `null` y la hoja sale sin retrato. Lo contrario sería que un
 * reporte de 600 km no se pueda entregar porque una miniatura dio 403.
 */

import logoBlancoUrl from "../../../../assets/wayak_logo_white.png";

import type { Bloque, DocumentoReporte } from "./tipos";

/**
 * El logo de WAYAK de la banda del encabezado: el mismo PNG blanco que usa el PDF
 * de facturación (`wayak-logo-white.png` del API). Es la clave con la que `construirPdf`
 * lo busca en el mapa de imágenes.
 */
export const LOGO_WAYAK = logoBlancoUrl;

/** Mapa de URL a `data:` listo para `addImage`. */
export type Imagenes = Map<string, string>;

/** Las URLs que el documento necesita incrustar. */
export function urlsDelDocumento(d: DocumentoReporte): string[] {
  const urls = new Set<string>();
  if (d.estilo === "factura") urls.add(LOGO_WAYAK);
  for (const b of d.bloques as Bloque[]) {
    if (b.tipo === "campos" && b.foto) urls.add(b.foto);
  }
  return [...urls];
}

async function aDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    return await new Promise<string | null>((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function resolverImagenes(d: DocumentoReporte): Promise<Imagenes> {
  const urls = urlsDelDocumento(d);
  const mapa: Imagenes = new Map();
  if (urls.length === 0) return mapa;

  const resueltas = await Promise.all(urls.map(aDataUrl));
  urls.forEach((u, i) => {
    const dato = resueltas[i];
    if (dato) mapa.set(u, dato);
  });
  return mapa;
}

/** `data:image/jpeg;base64,…` → `JPEG`, que es lo que pide jsPDF. */
export function formatoDe(dataUrl: string): "JPEG" | "PNG" | "WEBP" {
  const tipo = dataUrl.slice(5, dataUrl.indexOf(";")).toLowerCase();
  if (tipo.includes("png")) return "PNG";
  if (tipo.includes("webp")) return "WEBP";
  return "JPEG";
}
